import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import { 
  Sparkles, 
  Play, 
  ArrowRight, 
  Cpu, 
  Printer, 
  ScanLine, 
  Store,
  CheckCircle,
  Zap,
  Shield,
  Globe,
  TrendingUp,
  Users,
  Box,
  Layers,
  Target
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import BetaBadge from '@/components/ui/BetaBadge'
import MarketingNav from '@/components/marketing/MarketingNav'
import MarketingFooter from '@/components/marketing/MarketingFooter'

const features = [
  {
    icon: Cpu,
    title: 'AI-Native CAD',
    description: 'Design with natural language. Say "add a fillet to all edges" and watch it happen. Your AI co-pilot understands design intent.',
    gradient: 'from-primary-500 to-ai-500',
  },
  {
    icon: Printer,
    title: 'Print Intelligence',
    description: 'Know before you print. 92%+ accuracy predicting success, failures detected in seconds, not hours.',
    gradient: 'from-ai-500 to-success-500',
  },
  {
    icon: ScanLine,
    title: 'Scan to CAD',
    description: 'Point your phone at any object. Get an editable parametric model, not just a mesh. Reverse engineering for everyone.',
    gradient: 'from-success-500 to-warning-500',
  },
  {
    icon: Store,
    title: 'MakerHub Marketplace',
    description: 'Discover millions of models with AI-powered search. Sell your designs. Print-on-demand without inventory.',
    gradient: 'from-warning-500 to-primary-500',
  },
]

const stats = [
  { value: '92%', label: 'Print prediction accuracy', icon: Target },
  { value: '60%', label: 'Design time reduction', icon: TrendingUp },
  { value: '35%', label: 'First-print success boost', icon: CheckCircle },
  { value: '<10s', label: 'Failure detection', icon: Zap },
]

const capabilities = [
  'Text-to-3D generation',
  'Image-to-3D conversion', 
  'Real-time collaboration',
  'Universal printer support',
  'AI failure prediction',
  'Parametric editing',
  'Version control',
  'Material optimization',
]

const printerLogos = [
  'Prusa', 'Bambu Lab', 'Creality', 'Formlabs', 'Ultimaker', 'Anycubic'
]

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-surface-950">
      <MarketingNav />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 gradient-mesh opacity-50" />
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-ai-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ai-500/10 border border-ai-500/20 mb-8"
            >
              <Sparkles className="w-4 h-4 text-ai-400" />
              <span className="text-sm font-medium text-ai-300">3DMakerly - AI-Native 3D Manufacturing Platform</span>
              <BetaBadge size="sm" />
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-7xl font-bold text-white leading-tight"
            >
              Design. Print. Sell.
              <br />
              <span className="text-gradient">Powered by AI.</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-xl text-surface-300 max-w-2xl mx-auto leading-relaxed"
            >
              3DMakerly is the first CAD platform where AI isn't a feature - it's the foundation. 
              From sketch to print, every interaction is augmented by intelligence that 
              understands your creative intent.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/signup">
                <Button variant="ai" size="lg" className="text-base px-8">
                  <Sparkles className="w-5 h-5" />
                  Start Creating Free
                </Button>
              </Link>
              <Button variant="secondary" size="lg" className="text-base px-8">
                <Play className="w-5 h-5" />
                Watch Demo
              </Button>
            </motion.div>

            {/* Social Proof */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-12 flex items-center justify-center gap-8 text-surface-500"
            >
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span className="text-sm">Free during public beta</span>
              </div>
              <div className="hidden sm:block w-px h-6 bg-surface-700" />
              <div className="hidden sm:flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span className="text-sm">SOC 2 Compliant</span>
              </div>
              <div className="hidden sm:block w-px h-6 bg-surface-700" />
              <div className="hidden sm:flex items-center gap-2">
                <Globe className="w-5 h-5" />
                <span className="text-sm">Works Offline</span>
              </div>
            </motion.div>
          </div>

          {/* Hero Visual */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-20 relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-surface-700/50 shadow-2xl">
              <div className="aspect-[16/9] bg-surface-900 relative">
                {/* Mock App Interface */}
                <div className="absolute inset-0 flex">
                  {/* Sidebar */}
                  <div className="w-16 bg-surface-900 border-r border-surface-800 flex flex-col items-center py-4 gap-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-ai-500" />
                    <div className="w-8 h-8 rounded-lg bg-surface-800" />
                    <div className="w-8 h-8 rounded-lg bg-surface-800" />
                    <div className="w-8 h-8 rounded-lg bg-surface-800" />
                  </div>
                  
                  {/* Main Canvas */}
                  <div className="flex-1 relative bg-gradient-to-br from-surface-900 to-surface-950">
                    {/* 3D Grid */}
                    <div className="absolute inset-0 grid-pattern opacity-30" />
                    
                    {/* 3D Object Placeholder */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative">
                        <motion.div
                          animate={{ rotateY: 360 }}
                          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                          className="w-48 h-48 md:w-64 md:h-64"
                          style={{ transformStyle: 'preserve-3d' }}
                        >
                          <Box className="w-full h-full text-primary-500/30" strokeWidth={0.5} />
                        </motion.div>
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-ai-500/20 rounded-full blur-3xl" />
                      </div>
                    </div>
                    
                    {/* AI Command Bar */}
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="bg-surface-800/90 backdrop-blur-xl rounded-xl border border-surface-700/50 p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-ai-500/20">
                            <Sparkles className="w-5 h-5 text-ai-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-surface-400 text-sm">AI Command</p>
                            <p className="text-white">"Add 2mm fillets to all edges and export for PLA printing"</p>
                          </div>
                          <div className="px-3 py-1.5 rounded-lg bg-ai-500/20 text-ai-400 text-sm font-medium">
                            Processing...
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Panel */}
                  <div className="w-72 bg-surface-900/80 border-l border-surface-800 p-4 hidden lg:block">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-white font-medium">
                        <Layers className="w-4 h-4 text-ai-400" />
                        AI Analysis
                      </div>
                      <div className="space-y-3">
                        <div className="p-3 rounded-lg bg-surface-800/50">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-surface-400">Print Success</span>
                            <span className="text-success-400 font-medium">94%</span>
                          </div>
                          <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
                            <div className="h-full w-[94%] bg-gradient-to-r from-success-500 to-success-400 rounded-full" />
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-surface-800/50">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-surface-400">Est. Time</span>
                            <span className="text-white font-medium">2h 14m</span>
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-surface-800/50">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-surface-400">Material</span>
                            <span className="text-white font-medium">47g PLA</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Glow Effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-ai-500/20 to-primary-500/20 rounded-3xl blur-3xl -z-10" />
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 border-y border-surface-800 bg-surface-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-ai-500/10 mb-4">
                  <stat.icon className="w-6 h-6 text-ai-400" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-surface-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6"
            >
              <Zap className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-medium text-primary-300">Core Capabilities</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Everything you need to
              <br />
              <span className="text-gradient">make anything</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-lg text-surface-300"
            >
              From concept to physical product, 3DMakerly's AI-powered platform handles 
              every step of the additive manufacturing workflow.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative p-8 rounded-2xl bg-surface-900/50 border border-surface-800 hover:border-surface-700 transition-all duration-300"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-surface-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} mb-6`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-surface-300 leading-relaxed">{feature.description}</p>
                  <Link 
                    to="/features" 
                    className="inline-flex items-center gap-2 mt-6 text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    Learn more <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-24 bg-surface-900/30 border-y border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl font-bold text-white mb-6"
              >
                AI that speaks
                <br />
                <span className="text-gradient">maker language</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-lg text-surface-300 mb-8"
              >
                Built by makers, for makers. 3DMakerly's AI understands chamfers, not just edges. 
                It knows the difference between PLA and PETG. It predicts warping before 
                you waste filament.
              </motion.p>
              
              <div className="grid grid-cols-2 gap-4">
                {capabilities.map((capability, index) => (
                  <motion.div
                    key={capability}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0" />
                    <span className="text-surface-200">{capability}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="mt-10"
              >
                <Link to="/features">
                  <Button variant="primary" size="lg">
                    Explore All Features
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl bg-surface-800 border border-surface-700 overflow-hidden">
                {/* AI Conversation Mock */}
                <div className="p-6 h-full flex flex-col">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 rounded-lg bg-ai-500/20">
                      <Sparkles className="w-5 h-5 text-ai-400" />
                    </div>
                    <span className="font-medium text-white">3DMakerly Copilot</span>
                  </div>
                  
                  <div className="flex-1 space-y-4 overflow-hidden">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-surface-700 flex-shrink-0" />
                      <div className="bg-surface-700/50 rounded-2xl rounded-tl-none p-4 max-w-[80%]">
                        <p className="text-surface-200 text-sm">I need to design a phone stand that prints without supports</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 justify-end">
                      <div className="bg-gradient-to-br from-primary-600/20 to-ai-600/20 rounded-2xl rounded-tr-none p-4 max-w-[80%] border border-primary-500/20">
                        <p className="text-surface-200 text-sm">I'll create a phone stand with a 45° maximum overhang angle. What phone dimensions should I use?</p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-ai-500 flex-shrink-0 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-surface-700 flex-shrink-0" />
                      <div className="bg-surface-700/50 rounded-2xl rounded-tl-none p-4 max-w-[80%]">
                        <p className="text-surface-200 text-sm">iPhone 15 Pro, viewing angle around 60 degrees</p>
                      </div>
                    </div>

                    <div className="flex gap-3 justify-end">
                      <div className="bg-gradient-to-br from-primary-600/20 to-ai-600/20 rounded-2xl rounded-tr-none p-4 max-w-[80%] border border-primary-500/20">
                        <p className="text-surface-200 text-sm">Creating parametric stand for iPhone 15 Pro (146.6 x 70.6mm) with 60° viewing angle and support-free geometry...</p>
                        <div className="mt-3 flex items-center gap-2">
                          <div className="h-1.5 flex-1 bg-surface-700 rounded-full overflow-hidden">
                            <motion.div 
                              className="h-full bg-gradient-to-r from-primary-500 to-ai-500 rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: '75%' }}
                              transition={{ duration: 2, repeat: Infinity }}
                            />
                          </div>
                          <span className="text-xs text-surface-400">Generating...</span>
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-ai-500 flex-shrink-0 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -inset-4 bg-gradient-to-br from-primary-500/10 to-ai-500/10 rounded-3xl blur-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Printer Compatibility */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-lg text-surface-400 mb-4">Works with all major 3D printers</h3>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {printerLogos.map((brand) => (
                <div key={brand} className="text-surface-500 font-semibold text-lg hover:text-surface-300 transition-colors">
                  {brand}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-ai-600" />
            <div className="absolute inset-0 grid-pattern opacity-10" />
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
            
            <div className="relative px-8 py-16 md:p-20 text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to transform how you make?
              </h2>
              <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
                Join makers worldwide who are designing faster, printing smarter, 
                and selling their creations with AI-powered tools.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-white text-primary-600 hover:bg-white/90 text-base px-8">
                    <Sparkles className="w-5 h-5" />
                    Get Started Free
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="ghost" className="text-white border-white/20 hover:bg-white/10 text-base px-8">
                    View Pricing
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
